import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";

import { CPTCodeQuestionsLinkIds } from "@/encounters/constants/cpt-code";
import { getFormsetAnswers } from "@/utils/questionnaires";
import ProceduresForm, {
  ProceduresFormType,
  ProceduresFormTypeWithId,
} from "@/zod-schemas/procedures";

export const parseProcedureQuestionnaireResponse = (
  questionnaireResponses: QuestionnaireResponse[],
): ProceduresFormTypeWithId[] => {
  if (!questionnaireResponses || !questionnaireResponses?.length) return [];

  return questionnaireResponses.map((response) => {
    const answers = getQuestionnaireAnswers(response);
    const formsetAnswers = getFormsetAnswers(
      response,
      CPTCodeQuestionsLinkIds.DiagnosesFormset,
      CPTCodeQuestionsLinkIds.DiagnosesFormsetCode,
    );
    const modifiersFormsetAnswers = getFormsetAnswers(
      response,
      CPTCodeQuestionsLinkIds.ModifiersFormset,
      CPTCodeQuestionsLinkIds.ModifiersFormsetCode,
    );

    const cptCode = answers[CPTCodeQuestionsLinkIds.Code]?.valueCoding;
    const cptCodeNotes = answers[CPTCodeQuestionsLinkIds.Notes]?.valueString;
    const status = response.status;
    const cptUnits = answers[CPTCodeQuestionsLinkIds.Units]?.valueInteger;

    const cptModifiersFormset = Object.keys(modifiersFormsetAnswers).reduce((acc, key) => {
      return {
        ...acc,
        [key]: {
          cptModifiersFormsetCode: key,
          cptModifiersFormsetModifier:
            modifiersFormsetAnswers?.[key]?.[0]?.cptModifiersFormsetModifier?.valueString,
        },
      };
    }, {});

    const cptDiagnosesFormset = Object.keys(formsetAnswers).reduce((acc, key) => {
      return {
        ...acc,
        [key]: {
          cptDiagnosesFormsetCode: key,
          cptDiagnosesFormsetDiagnosis:
            formsetAnswers?.[key]?.[0]?.cptDiagnosesFormsetDiagnosis?.valueCoding,
          cptDiagnosesFormsetAddToProblemList:
            formsetAnswers?.[key]?.[0]?.cptDiagnosesFormsetAddToProblemList?.valueBoolean ?? false,
        },
      };
    }, {});

    const parsedForm = ProceduresForm.parse({
      cptCode,
      cptCodeNotes,
      cptDiagnosesFormset,
      cptUnits,
      cptModifiersFormset,
    });

    return {
      ...parsedForm,
      id: response.id!,
      exists: true,
      status,
    };
  });
};

export function toProceduresFormValues(
  procedure: ProceduresFormTypeWithId | undefined,
): ProceduresFormType {
  if (!procedure) {
    return ProceduresForm.parse({});
  }

  const parsed = ProceduresForm.safeParse({
    cptCode: procedure.cptCode,
    cptCodeNotes: procedure.cptCodeNotes,
    cptDiagnosesFormset: procedure.cptDiagnosesFormset,
    cptUnits: procedure.cptUnits,
    cptModifiersFormset: procedure.cptModifiersFormset,
  });

  if (!parsed.success) {
    console.warn(
      "[toProceduresFormValues] Invalid procedure shape; falling back to empty form",
      procedure.id,
      parsed.error.flatten(),
    );
    return ProceduresForm.parse({});
  }

  return parsed.data;
}
